// Client side of game tables: one subscription per placed table, shared by
// the 3D table, the DOM overlay and the VR panel.
//
// The public state streams in over Realtime (postgres_changes on the
// table's row); your own private state (cards, tiles, the secret word — or
// the host's answers) is read from game_hands, where RLS only returns your
// row. Every change goes through the `games` edge function.

import { create } from 'zustand';
import { supabase } from '../../config/supabase';
import { HOST, type TableState, type Seat } from '../../../supabase/functions/games/types.ts';

export { HOST };
export type { TableState, Seat };
export interface TableView { id: string; kind: string; state: TableState; version: number }

export interface TableEntry {
  worldId: string;
  primitiveId: string;
  kind: string;
  table: TableView | null;
  /** My private state at this table (seat or host row), null if none. Spades: string[] (the hand). */
  priv: any;
  error: string | null;
  busy: boolean;
}
type Entry = TableEntry;

interface GamesState {
  me: string | null;
  tables: Record<string, Entry>; // by primitive id
  /** Primitive id of the table the player is standing at (or seated at). */
  near: string | null;
  setNear: (id: string | null) => void;
}

export const useGames = create<GamesState>((set) => ({
  me: null,
  tables: {},
  near: null,
  setNear: (near) => set((s) => (s.near === near ? s : { near })),
}));
if (import.meta.env.DEV) (window as any).__gathrxrGames = useGames;

// ---- which table you're "at" ---------------------------------------------
// Every table reports a claim each frame: a score, lower wins, or Infinity
// for none. Tables you're standing at score by distance as a fraction of
// their radius (capped at 4 m), so the dominoes table you're standing at
// beats a trivia set whose 14 m room you're also inside. A table where you hold a seat or host
// still claims you from anywhere, but only when you're not standing at
// another one.
const claims = new Map<string, number>();
export function claimNear(id: string, score: number) {
  if (score === Infinity) claims.delete(id); else claims.set(id, score);
  let best: string | null = null, bestScore = Infinity;
  for (const [k, v] of claims) if (v < bestScore) { best = k; bestScore = v; }
  useGames.getState().setNear(best);
}

let meLoaded = false;
async function loadMe() {
  if (meLoaded) return;
  meLoaded = true;
  const { data } = await supabase.auth.getSession();
  useGames.setState({ me: data.session?.user?.id ?? null });
  supabase.auth.onAuthStateChange((_e, session) => useGames.setState({ me: session?.user?.id ?? null }));
}

const patch = (pid: string, p: Partial<Entry>) =>
  useGames.setState((s) => (s.tables[pid] ? { tables: { ...s.tables, [pid]: { ...s.tables[pid], ...p } } } : s));

export async function gameCall(body: Record<string, unknown>): Promise<TableView> {
  const { data, error } = await supabase.functions.invoke('games', { body });
  if (error) {
    // supabase-js wraps non-2xx answers; the function's { error } is in the response body.
    let msg = error.message || 'Request failed';
    try {
      const ctx = (error as any).context;
      if (ctx && typeof ctx.json === 'function') { const j = await ctx.json(); if (j?.error) msg = j.error; }
    } catch { /* keep the generic message */ }
    throw new Error(msg);
  }
  if (!data?.table) throw new Error(data?.error || 'No table in response');
  return data.table as TableView;
}

/** My seat index at a table, or −1. */
export function mySeat(table: TableView | null, me: string | null): number {
  if (!table || !me) return -1;
  return table.state.seats.findIndex((s) => s?.kind === 'human' && s.userId === me);
}

/** Am I the table's host? */
export function amHost(table: TableView | null, me: string | null): boolean {
  const h = table?.state.host;
  return !!me && h?.kind === 'human' && h.userId === me;
}

/** My private state as a list (card games), [] if none. */
export function handOf(entry: TableEntry | undefined | null): string[] {
  return Array.isArray(entry?.priv) ? (entry!.priv as string[]) : [];
}

/** When the current human turn auto-plays (idle timeout), or null. For turn timers. */
export function turnDeadline(st: TableState | undefined | null): number | null {
  return st && st.phase === 'playing' && st.dueKind === 'idle' ? st.dueAt : null;
}

function accept(pid: string, t: TableView) {
  const cur = useGames.getState().tables[pid]?.table;
  if (cur && cur.id === t.id && cur.version > t.version) return; // stale
  patch(pid, { table: t, error: null });
  void refreshPriv(pid);
}

async function refreshPriv(pid: string) {
  const e = useGames.getState().tables[pid];
  const me = useGames.getState().me;
  if (!e?.table) return;
  const seat = amHost(e.table, me) ? HOST : mySeat(e.table, me);
  if ((seat < 0 && seat !== HOST) || e.table.state.phase === 'lobby') { if (e.priv !== null) patch(pid, { priv: null }); return; }
  const { data } = await supabase.from('game_hands').select('cards').eq('table_id', e.table.id).eq('seat', seat).maybeSingle();
  patch(pid, { priv: data?.cards ?? null });
}

const subs = new Map<string, { refs: number; stop: () => void }>();

/** Follow a table while a component needs it. Returns an unsubscribe. */
export function followTable(worldId: string, primitiveId: string, kind: string): () => void {
  const existing = subs.get(primitiveId);
  if (existing) { existing.refs++; return () => release(primitiveId); }
  void loadMe();
  useGames.setState((s) => ({
    tables: { ...s.tables, [primitiveId]: s.tables[primitiveId] ?? { worldId, primitiveId, kind, table: null, priv: null, error: null, busy: false } },
  }));
  let stopped = false;
  let channel: ReturnType<typeof supabase.channel> | null = null;
  let lastTick = 0;
  let rounds = 0;

  const load = async () => {
    try {
      const t = await gameCall({ action: 'get', worldId, primitiveId, kind });
      if (stopped) return;
      accept(primitiveId, t);
      if (!channel) {
        channel = supabase
          .channel(`game-table:${t.id}`)
          .on('postgres_changes' as any, { event: 'UPDATE', schema: 'public', table: 'game_tables', filter: `id=eq.${t.id}` },
            (payload: any) => {
              const row = payload.new;
              if (row?.state) accept(primitiveId, { id: row.id, kind: row.kind, state: row.state, version: row.version });
            })
          .subscribe();
      }
    } catch (err: any) {
      if (!stopped) patch(primitiveId, { error: err?.message || 'Could not load the table' });
    }
  };
  void load();

  // Fallback poll (missed realtime events) + nudge the server when a bot
  // move, the game clock or the next deal is overdue — the server's own
  // chain normally handles these; this guarantees a table can't stall. Game
  // clocks (question / round timers) more than 15 s out are only ever
  // advanced by this nudge, so those fire promptly.
  const timer = window.setInterval(async () => {
    const e = useGames.getState().tables[primitiveId];
    const t = e?.table;
    if (!t) { if (++rounds % 4 === 0) void load(); return; }
    const now = Date.now();
    const wake = t.state.dueKind === 'wake';
    if (t.state.phase === 'playing' && t.state.dueAt && now > t.state.dueAt + (wake ? 400 : 2500) && now - lastTick > (wake ? 1500 : 4000)) {
      lastTick = now;
      try { accept(primitiveId, await gameCall({ action: 'tick', tableId: t.id })); } catch { /* next round */ }
      return;
    }
    if (++rounds % 20 === 0) {
      const { data } = await supabase.from('game_tables').select('id, kind, state, version').eq('id', t.id).maybeSingle();
      if (data) accept(primitiveId, data as TableView);
    }
  }, 500);

  subs.set(primitiveId, {
    refs: 1,
    stop: () => {
      stopped = true;
      window.clearInterval(timer);
      if (channel) supabase.removeChannel(channel);
    },
  });
  return () => release(primitiveId);
}

function release(pid: string) {
  const s = subs.get(pid);
  if (!s) return;
  if (--s.refs > 0) return;
  s.stop();
  subs.delete(pid);
}

/** Send an action for the table at `pid`; errors land in the entry. */
export async function tableAction(pid: string, action: string, extra: Record<string, unknown> = {}): Promise<TableView | null> {
  const e = useGames.getState().tables[pid];
  if (!e?.table) return null;
  patch(pid, { busy: true, error: null });
  try {
    const t = await gameCall({ action, tableId: e.table.id, ...extra });
    accept(pid, t);
    return t;
  } catch (err: any) {
    patch(pid, { error: err?.message || 'That did not work' });
    return null;
  } finally {
    patch(pid, { busy: false });
  }
}

// ---- seating the avatar --------------------------------------------------

/** Registry of chair seat markers per table, filled by the 3D table. */
export const chairMarkers = new Map<string, (import('three').Object3D | null)[]>();

/** Walk the avatar (desktop) or move the rig (VR) into the chair of `seat`. */
export function sitInChair(pid: string, seat: number) {
  const marker = chairMarkers.get(pid)?.[seat];
  if (marker) window.dispatchEvent(new CustomEvent('gathrxr:game-sit', { detail: { seat: marker } }));
}
export function standFromChair() {
  window.dispatchEvent(new CustomEvent('gathrxr:game-stand'));
}
